import React from 'react';
import { Link } from 'react-router-dom';
import { 
  EnvelopeIcon, 
  PhoneIcon,
  MapPinIcon 
} from '@heroicons/react/24/outline';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'מוצרים',
      links: [
        { name: 'כל המוצרים', href: '/products' },
        { name: 'מוצרים מובילים', href: '/products?featured=true' },
        { name: 'מבצעים', href: '/products?sale=true' },
        { name: 'חדש באתר', href: '/products?sort=newest' },
      ],
    },
    {
      title: 'שירות לקוחות',
      links: [
        { name: 'צור קשר', href: '/contact' },
        { name: 'משלוחים והחזרות', href: '/shipping' },
        { name: 'תנאי שימוש', href: '/terms' },
        { name: 'מדיניות פרטיות', href: '/privacy' },
      ],
    },
    {
      title: 'החשבון שלי',
      links: [
        { name: 'התחבר', href: '/login' },
        { name: 'הרשם', href: '/register' },
        { name: 'ההזמנות שלי', href: '/orders' },
        { name: 'הפרופיל שלי', href: '/account' },
      ],
    },
    {
      title: 'על החברה',
      links: [
        { name: 'אודות', href: '/about' },
        { name: 'הבלוג שלנו', href: '/blog' },
        { name: 'קריירה', href: '/careers' },
        { name: 'מדיה', href: '/media' },
      ],
    },
  ];

  return (
    <footer id="footer" className="bg-gray-900 text-white" role="contentinfo" aria-label="תחתית האתר">
      <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          {/* Company info */}
          <div className="space-y-8">
            <div className="flex items-center">
              <img 
                src="/lumel-logo.jpeg" 
                alt="LUMEL Elegant Essence" 
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-sm leading-6 text-gray-300">
              LUMEL - Elegant Essence. אנחנו מספקים פתרונות מקצועיים 
              לעסקים ולקוחות פרטיים עם שירות ללא פשרות.
            </p>
            
            {/* Contact info */}
            <div className="space-y-2" role="region" aria-label="פרטי יצירת קשר">
              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-gray-400 ml-2" aria-hidden="true" />
                <a href="tel:03-123-4567" className="text-sm text-gray-300 hover:text-white">03-123-4567</a>
              </div>
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 ml-2" aria-hidden="true" />
                <a href="mailto:info@b2bstore.co.il" className="text-sm text-gray-300 hover:text-white">info@b2bstore.co.il</a>
              </div>
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-gray-400 ml-2" aria-hidden="true" />
                <span className="text-sm text-gray-300">תל אביב, ישראל</span>
              </div>
            </div>

            {/* Social media */}
            <nav className="flex space-x-6 space-x-reverse" aria-label="קישורים לרשתות חברתיות">
              <a href="#" className="text-gray-400 hover:text-primary-400" aria-label="עקוב אחרינו בפייסבוק">
                <span className="sr-only">פייסבוק</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-primary-400" aria-label="עקוב אחרינו באינסטגרם">
                <span className="sr-only">אינסטגרם</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.329-1.295C4.198 14.553 3.5 13.117 3.5 11.5s.698-3.053 1.62-4.193c.881-.805 2.032-1.295 3.329-1.295s2.448.49 3.329 1.295C12.7 8.447 13.398 9.883 13.398 11.5s-.698 3.053-1.62 4.193c-.881.805-2.032 1.295-3.329 1.295z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-primary-400" aria-label="עקוב אחרינו בלינקדאין">
                <span className="sr-only">לינקדאין</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </nav>
          </div>

          {/* Footer links */}
          <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <nav aria-label={footerSections[0].title}>
                <h3 className="text-sm font-semibold leading-6 text-white">
                  {footerSections[0].title}
                </h3>
                <ul className="mt-6 space-y-4" role="list">
                  {footerSections[0].links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-sm leading-6 text-gray-300 hover:text-white transition-colors duration-200"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
              <nav className="mt-10 md:mt-0" aria-label={footerSections[1].title}>
                <h3 className="text-sm font-semibold leading-6 text-white">
                  {footerSections[1].title}
                </h3>
                <ul className="mt-6 space-y-4" role="list">
                  {footerSections[1].links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-sm leading-6 text-gray-300 hover:text-white transition-colors duration-200"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <nav aria-label={footerSections[2].title}>
                <h3 className="text-sm font-semibold leading-6 text-white">
                  {footerSections[2].title}
                </h3>
                <ul className="mt-6 space-y-4" role="list">
                  {footerSections[2].links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-sm leading-6 text-gray-300 hover:text-white transition-colors duration-200"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
              <nav className="mt-10 md:mt-0" aria-label={footerSections[3].title}>
                <h3 className="text-sm font-semibold leading-6 text-white">
                  {footerSections[3].title}
                </h3>
                <ul className="mt-6 space-y-4" role="list">
                  {footerSections[3].links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-sm leading-6 text-gray-300 hover:text-white transition-colors duration-200"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        </div>

        {/* Bottom section */}
        <div className="mt-16 border-t border-gray-800 pt-8 sm:mt-20 lg:mt-24">
          <div className="sm:flex sm:justify-between sm:items-center">
            <p className="text-xs leading-5 text-gray-400">
              &copy; {currentYear} LUMEL - Elegant Essence. כל הזכויות שמורות.
            </p>
            <div className="mt-4 sm:mt-0">
              <p className="text-xs leading-5 text-gray-400">
                עם ❤️ נבנה בישראל
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
